import {
  Funnel,
  FunnelConversation,
  type TCSearcherTimelineItemDocument as SearcherTimelineItemDocument,
} from 'src/api/generated';
import { getConversationIdFromItem } from '../../utils/timeline/fields-getter.util';
import { useFunnelConversation } from 'src/modules/communications/hooks/api/funnels/funnel-conversations';
import { useFunnel } from 'src/modules/shared/hooks/api/crm/funnels/funnels';

/**
 * Context information for a timeline item
 */
export interface TimelineItemContext {
  otherFunnel?: Funnel;
  otherConversation?: FunnelConversation;
  loading?: boolean;
}

interface TimelineItemContextParams {
  item: SearcherTimelineItemDocument;
  currentFunnelId?: number;
  currentConversationId?: number;
}

/**
 * Derives cross-context indicators for a timeline item, determining whether the
 * item belongs to a different funnel or conversation than the current view.
 */
export function useTimelineItemContext({
  item,
  currentFunnelId,
  currentConversationId,
}: TimelineItemContextParams): TimelineItemContext {
  const itemConversationId = getConversationIdFromItem(item);

  const { conversation: itemConversation, loading: loadingConversation } =
    useFunnelConversation(
      {
        id: itemConversationId ?? 0,
      },
      !!itemConversationId
    );

  const conversationFunnelId = itemConversation?.funnelId ?? undefined;
  const { funnel, loading: loadingFunnel } = useFunnel({
    id: conversationFunnelId ?? 0,
    enabled: !!conversationFunnelId,
  });

  const isOtherFunnel =
    !!currentFunnelId &&
    !!conversationFunnelId &&
    conversationFunnelId !== currentFunnelId;

  const isOtherConversation =
    !!currentConversationId &&
    !!itemConversationId &&
    itemConversationId !== currentConversationId;

  const otherFunnel = isOtherFunnel ? funnel ?? undefined : undefined;
  const otherConversation = isOtherConversation
    ? itemConversation ?? undefined
    : undefined;

  return {
    otherFunnel,
    otherConversation,
    loading: loadingFunnel || loadingConversation,
  };
}
